import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Layout, Plus, Eye, Grid3X3 } from 'lucide-react';
import { motion } from 'motion/react';
import { layoutOptions } from './SlideTemplates';
import { Template } from './TemplateSelector';

interface LayoutSelectorProps {
  onSelectLayout: (layoutId: string, slideType: string) => void;
  selectedTemplate: Template;
  onAddSlide?: (slideData: any) => void;
}

const LayoutSelector: React.FC<LayoutSelectorProps> = ({
  onSelectLayout,
  selectedTemplate,
  onAddSlide
}) => {
  const [selectedLayout, setSelectedLayout] = useState<string>('');
  const [hoveredLayout, setHoveredLayout] = useState<string>('');

  const handleLayoutSelect = (layoutId: string) => {
    setSelectedLayout(layoutId);
    const layout = layoutOptions.find((l) => l.id === layoutId);
    if (layout) {
      onSelectLayout(layoutId, layout.category.toLowerCase());

      // If onAddSlide is provided, create a new slide with this layout
      if (onAddSlide) {
        const newSlide = {
          id: `slide-${Date.now()}`,
          title: getDefaultTitle(layout),
          content: getDefaultContent(layout),
          type: getSlideType(layout),
          notes: '',
          template: selectedTemplate,
          imageUrl: shouldHaveImage(layout) ? generateImageUrl(layout.name) : undefined,
          imageAlt: `${layout.name} - ${layout.description}`
        };
        onAddSlide(newSlide);
      }
    }
  };

  const shouldHaveImage = (layout: any): boolean => {
    return ['content-image', 'title-slide'].includes(layout.id);
  };

  const generateImageUrl = (keywords: string): string => {
    const cleanKeywords = keywords.
    toLowerCase().
    replace(/[^a-zA-Z0-9\s]/g, '').
    replace(/\s+/g, ',').
    slice(0, 100);
    return `https://source.unsplash.com/1200x800/?${cleanKeywords || 'education'}`;
  };

  const getDefaultTitle = (layout: any) => {
    switch (layout.id) {
      case 'title-slide':return 'Presentation Title';
      case 'content-bullets':return 'Key Points';
      case 'two-content':return 'Two Column Content';
      case 'content-image':return 'Content with Image';
      case 'centered':return 'Important Message';
      case 'quote':return 'Inspiring Quote';
      case 'comparison':return 'Comparison';
      case 'conclusion':return 'Conclusion';
      default:return 'New Slide';
    }
  };

  const getDefaultContent = (layout: any) => {
    switch (layout.id) {
      case 'title-slide':return 'Subtitle or description goes here\n\nPresented by [Your Name]';
      case 'content-bullets':return 'First key point\nSecond key point\nThird key point\nFourth key point';
      case 'two-content':return 'Left column content goes here||Right column content goes here';
      case 'content-image':return 'Your content description goes here.\n\nThis layout works great for explaining concepts with visual support.';
      case 'centered':return 'This is a centered message that stands out.\n\nUse this layout for important announcements or key takeaways.';
      case 'quote':return 'Your inspiring quote goes here';
      case 'comparison':return 'Option A details\nBenefits and features\n\nOption B details\nBenefits and features';
      case 'conclusion':return 'Summary of key points\n\nNext steps or call to action';
      default:return 'Add your content here';
    }
  };

  const getSlideType = (layout: any): string => {
    switch (layout.id) {
      case 'title-slide':return 'title';
      case 'content-bullets':return 'list';
      case 'two-content':return 'content';
      case 'content-image':return 'image-text';
      case 'centered':return 'content';
      case 'quote':return 'quote';
      case 'comparison':return 'comparison';
      case 'conclusion':return 'conclusion';
      default:return 'content';
    }
  };

  // Preview component for layout
  const LayoutPreview: React.FC<{layout: any;}> = ({ layout }) =>
  <div className="w-full h-32 bg-gray-50 rounded border-2 border-gray-200 p-2 relative overflow-hidden" data-id="rb62c29e1" data-path="src/components/ai/LayoutSelector.tsx">
      {layout.id === 'title-slide' &&
    <div className="text-center h-full flex flex-col justify-center" data-id="84w5oc9v0" data-path="src/components/ai/LayoutSelector.tsx">
          <div className="h-2 bg-gray-300 rounded mb-2 mx-4" data-id="3uxb8vlq0" data-path="src/components/ai/LayoutSelector.tsx"></div>
          <div className="h-1 bg-gray-200 rounded mx-8" data-id="oqg3i2yb5" data-path="src/components/ai/LayoutSelector.tsx"></div>
        </div>
    }
      
      {layout.id === 'content-bullets' &&
    <div className="h-full" data-id="ta59cqpsk" data-path="src/components/ai/LayoutSelector.tsx">
          <div className="h-2 bg-gray-300 rounded mb-2 w-3/4" data-id="c2msjqrvt" data-path="src/components/ai/LayoutSelector.tsx"></div>
          <div className="space-y-1" data-id="brrjsp5oe" data-path="src/components/ai/LayoutSelector.tsx">
            {[1, 2, 3, 4].map((i) =>
        <div key={i} className="flex items-center space-x-1" data-id="gkd40gmug" data-path="src/components/ai/LayoutSelector.tsx">
                <div className="w-1 h-1 bg-gray-400 rounded-full" data-id="lu35e6ynh" data-path="src/components/ai/LayoutSelector.tsx"></div>
                <div className="h-1 bg-gray-200 rounded flex-1" data-id="er5x4fl72" data-path="src/components/ai/LayoutSelector.tsx"></div>
              </div>
        )}
          </div>
        </div>
    }
      
      {layout.id === 'two-content' &&
    <div className="h-full" data-id="wtgcl3mz6" data-path="src/components/ai/LayoutSelector.tsx">
          <div className="h-2 bg-gray-300 rounded mb-2 w-2/3" data-id="zpdu6ha5r" data-path="src/components/ai/LayoutSelector.tsx"></div>
          <div className="grid grid-cols-2 gap-2 h-3/4" data-id="u0bevqhj5" data-path="src/components/ai/LayoutSelector.tsx">
            <div className="space-y-1" data-id="m86s786uo" data-path="src/components/ai/LayoutSelector.tsx">
              {[1, 2, 3].map((i) =>
          <div key={i} className="h-1 bg-gray-200 rounded" data-id="afdhp3a70" data-path="src/components/ai/LayoutSelector.tsx"></div>
          )}
            </div>
            <div className="space-y-1" data-id="agcunljrt" data-path="src/components/ai/LayoutSelector.tsx">
              {[1, 2, 3].map((i) =>
          <div key={i} className="h-1 bg-gray-200 rounded" data-id="yjqge7hl6" data-path="src/components/ai/LayoutSelector.tsx"></div>
          )}
            </div>
          </div>
        </div>
    }
      
      {layout.id === 'content-image' &&
    <div className="h-full flex" data-id="gm3e0bjbn" data-path="src/components/ai/LayoutSelector.tsx">
          <div className="w-1/2 pr-1" data-id="l8s9xads0" data-path="src/components/ai/LayoutSelector.tsx">
            <div className="h-2 bg-gray-300 rounded mb-1" data-id="u00pjb67b" data-path="src/components/ai/LayoutSelector.tsx"></div>
            <div className="space-y-1" data-id="7sn7484xy" data-path="src/components/ai/LayoutSelector.tsx">
              {[1, 2, 3].map((i) =>
          <div key={i} className="h-1 bg-gray-200 rounded" data-id="qw5j1t4zt" data-path="src/components/ai/LayoutSelector.tsx"></div>
          )}
            </div>
          </div>
          <div className="w-1/2 pl-1" data-id="uoul9600h" data-path="src/components/ai/LayoutSelector.tsx">
            <div className="w-full h-full bg-gray-300 rounded" data-id="7twbit9o1" data-path="src/components/ai/LayoutSelector.tsx"></div>
          </div>
        </div>
    }
      
      {layout.id === 'centered' &&
    <div className="h-full flex flex-col justify-center items-center" data-id="vpyfn0yyu" data-path="src/components/ai/LayoutSelector.tsx">
          <div className="h-2 bg-gray-300 rounded mb-2 w-3/4" data-id="ll8wsc141" data-path="src/components/ai/LayoutSelector.tsx"></div>
          <div className="h-1 bg-gray-200 rounded w-2/3" data-id="tawjbbict" data-path="src/components/ai/LayoutSelector.tsx"></div>
          <div className="h-1 bg-gray-200 rounded w-1/2 mt-1" data-id="xeiz3gzn2" data-path="src/components/ai/LayoutSelector.tsx"></div>
        </div>
    }
      
      {layout.id === 'quote' &&
    <div className="h-full flex flex-col justify-center items-center" data-id="53gfzfgyy" data-path="src/components/ai/LayoutSelector.tsx">
          <div className="text-2xl text-gray-400 mb-1" data-id="4rp2yeckl" data-path="src/components/ai/LayoutSelector.tsx">"</div>
          <div className="h-1 bg-gray-300 rounded w-3/4 mb-1" data-id="oha8byppg" data-path="src/components/ai/LayoutSelector.tsx"></div>
          <div className="h-1 bg-gray-300 rounded w-2/3 mb-2" data-id="ckrl6t0vk" data-path="src/components/ai/LayoutSelector.tsx"></div>
          <div className="h-1 bg-gray-200 rounded w-1/3" data-id="7k18e4i4v" data-path="src/components/ai/LayoutSelector.tsx"></div>
        </div>
    }
      
      {layout.id === 'comparison' &&
    <div className="h-full" data-id="wq2awv96a" data-path="src/components/ai/LayoutSelector.tsx">
          <div className="h-2 bg-gray-300 rounded mb-2 w-2/3 mx-auto" data-id="yvagq6zz4" data-path="src/components/ai/LayoutSelector.tsx"></div>
          <div className="grid grid-cols-3 gap-1 h-3/4" data-id="a3m5dj83k" data-path="src/components/ai/LayoutSelector.tsx">
            <div className="bg-gray-200 rounded" data-id="4q783mxum" data-path="src/components/ai/LayoutSelector.tsx"></div>
            <div className="flex items-center justify-center" data-id="ey3acp6id" data-path="src/components/ai/LayoutSelector.tsx">
              <div className="w-4 h-4 bg-gray-400 rounded-full text-xs flex items-center justify-center text-white font-bold" data-id="je3qe1266" data-path="src/components/ai/LayoutSelector.tsx">VS</div>
            </div>
            <div className="bg-gray-200 rounded" data-id="5iz87fims" data-path="src/components/ai/LayoutSelector.tsx"></div>
          </div>
        </div>
    }
      
      {layout.id === 'conclusion' &&
    <div className="h-full flex flex-col justify-center items-center" data-id="iyghlldx3" data-path="src/components/ai/LayoutSelector.tsx">
          <div className="h-2 bg-gray-300 rounded mb-2 w-2/3" data-id="vyth1begp" data-path="src/components/ai/LayoutSelector.tsx"></div>
          <div className="h-1 bg-gray-200 rounded w-3/4 mb-2" data-id="l4g25m7tt" data-path="src/components/ai/LayoutSelector.tsx"></div>
          <div className="h-2 bg-yellow-300 rounded w-1/2" data-id="ikpov6rrg" data-path="src/components/ai/LayoutSelector.tsx"></div>
        </div>
    }
      
      {/* Layout icon overlay */}
      <div className="absolute top-1 right-1 text-xs" data-id="zh61npxue" data-path="src/components/ai/LayoutSelector.tsx">
        {layout.icon}
      </div>
    </div>;

  const categories = [...new Set(layoutOptions.map((l) => l.category))];

  return (
    <div className="space-y-6" data-id="0v223gc3g" data-path="src/components/ai/LayoutSelector.tsx">
      <div className="text-center space-y-2" data-id="ff2lvta9a" data-path="src/components/ai/LayoutSelector.tsx">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-2" data-id="dkrrgjbfz" data-path="src/components/ai/LayoutSelector.tsx">
          <Grid3X3 className="h-6 w-6 text-blue-600" data-id="vdz5vspqm" data-path="src/components/ai/LayoutSelector.tsx" />
          Choose Slide Layout
        </h2>
        <p className="text-gray-600" data-id="hi3ozpg7n" data-path="src/components/ai/LayoutSelector.tsx">
          Select a layout template for your new slide
        </p>
      </div>

      <Tabs defaultValue={categories[0]} className="w-full" data-id="a22jfe2v1" data-path="src/components/ai/LayoutSelector.tsx">
        <TabsList className="grid w-full grid-cols-4" data-id="paok9yc59" data-path="src/components/ai/LayoutSelector.tsx">
          {categories.map((category) =>
          <TabsTrigger key={category} value={category} data-id="m2lyzi3ap" data-path="src/components/ai/LayoutSelector.tsx">
              {category}
            </TabsTrigger>
          )}
        </TabsList>

        {categories.map((category) =>
        <TabsContent key={category} value={category} className="mt-6" data-id="jiivmnqej" data-path="src/components/ai/LayoutSelector.tsx">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="4u4s6bv1a" data-path="src/components/ai/LayoutSelector.tsx">
              {layoutOptions.
            filter((layout) => layout.category === category).
            map((layout) =>
            <motion.div
              key={layout.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="group" data-id="3kpuw1j9y" data-path="src/components/ai/LayoutSelector.tsx">

                    <Card
                className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                selectedLayout === layout.id ?
                'ring-2 ring-blue-500 bg-blue-50' :
                'hover:shadow-md'}`
                }
                onClick={() => handleLayoutSelect(layout.id)}
                onMouseEnter={() => setHoveredLayout(layout.id)}
                onMouseLeave={() => setHoveredLayout('')} data-id="0tax2yx3q" data-path="src/components/ai/LayoutSelector.tsx">

                      <CardContent className="p-3" data-id="jihokh69m" data-path="src/components/ai/LayoutSelector.tsx">
                        <LayoutPreview layout={layout} data-id="g6r4mh75t" data-path="src/components/ai/LayoutSelector.tsx" />
                        
                        <div className="mt-3 space-y-1" data-id="bcer47462" data-path="src/components/ai/LayoutSelector.tsx">
                          <div className="flex items-center justify-between" data-id="nxp92wvgr" data-path="src/components/ai/LayoutSelector.tsx">
                            <h3 className="font-medium text-sm" data-id="tsb5uweva" data-path="src/components/ai/LayoutSelector.tsx">{layout.name}</h3>
                            {selectedLayout === layout.id &&
                      <div className="w-2 h-2 bg-blue-500 rounded-full" data-id="nu5kb9cw5" data-path="src/components/ai/LayoutSelector.tsx"></div>
                      }
                          </div>
                          <p className="text-xs text-gray-600" data-id="l14b1vc9j" data-path="src/components/ai/LayoutSelector.tsx">{layout.description}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
            )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Quick add buttons */}
      <div className="bg-gray-50 p-4 rounded-lg" data-id="gi6162vsy" data-path="src/components/ai/LayoutSelector.tsx">
        <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="luy9t1gzb" data-path="src/components/ai/LayoutSelector.tsx">Quick Add Common Layouts</h3>
        <div className="flex flex-wrap gap-2" data-id="707r8u64h" data-path="src/components/ai/LayoutSelector.tsx">
          {['title-slide', 'content-bullets', 'content-image', 'conclusion'].map((layoutId) => {
            const layout = layoutOptions.find((l) => l.id === layoutId);
            return layout ?
            <Button
              key={layoutId}
              variant="outline"
              size="sm"
              onClick={() => handleLayoutSelect(layoutId)}
              className="text-xs" data-id="xadkctm26" data-path="src/components/ai/LayoutSelector.tsx">
                {layout.icon} {layout.name}
              </Button> :
            null;
          })}
        </div>
      </div>

      {selectedLayout &&
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="4cmd3990o" data-path="src/components/ai/LayoutSelector.tsx">
          <p className="text-blue-800 font-medium" data-id="ace02td0p" data-path="src/components/ai/LayoutSelector.tsx">
            ✓ Layout "{layoutOptions.find((l) => l.id === selectedLayout)?.name}" selected
          </p>
          <p className="text-blue-600 text-sm" data-id="62am6ns5n" data-path="src/components/ai/LayoutSelector.tsx">
            This layout will be applied to your new slide
          </p>
        </motion.div>
      }
    </div>);
};

export default LayoutSelector;